import type { FC } from 'react';
import React, { useEffect, useCallback, useContext, useState } from 'react';
// We have deprecated emotion. Please use compiled instead
// eslint-disable-next-line no-restricted-imports, @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import styled from '@emotion/styled';

import { RightSidebar as RightSidebarPanel } from '@atlaskit/page-layout';

import {
	ConfluenceConversationAssistant,
	CONVERSATION_ASSISTANT_CONTAINER_ID,
	CONVERSATION_ASSISTANT_CONTAINER_WIDTH,
} from '@confluence/conversation-assistant';
import {
	VIEW_IN_PRODUCT_HELP_EXPERIENCE,
	ExperienceTrackerContext,
} from '@confluence/experience-tracker';
import { Attribution, ErrorBoundary } from '@confluence/error-boundary';
import {
	HelpPanel,
	HELP_PANEL_ID,
	HELP_PANEL_WIDTH,
	useInProductHelpActions,
	useInProductHelpArticleId,
	getRouteData,
} from '@confluence/in-product-help';
import { useRightSidebarContext } from '@confluence/page-layout-context';
import { RoutesContext } from '@confluence/route-manager/entry-points/RoutesContext';
import { confluenceLocalStorageInstance, keys } from '@confluence/storage-manager';
import { useQuickstartState } from '@confluence/onboarding-quickstart';
import { NotesSidebar } from '@confluence/notes-sidebar/entry-points/NotesSidebar';
import { NOTES_PANEL_ID, NOTES_SIDEBAR_WIDTH } from '@confluence/notes-util/entry-points/constants';
import { useBooleanFeatureFlag, useSessionData } from '@confluence/session-data';
import {
	IssueCreateSidePanel,
	ISSUE_CREATE_SIDE_PANEL_ID,
} from '@confluence/issue-create-side-panel';

export type RightSidebarProps = {
	toggleInProductHelp: (val) => void;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/design-system/no-styled-tagged-template-expression -- Ignored via go/DSP-18766
const RightSidebarPanelWrapper = styled.div`
	display: contents;
	& > :first-of-type {
		transition: width 300ms cubic-bezier(0.2, 0, 0, 1) 0s;
		z-index: 12;
	}
`;

export const RightSidebar: FC<RightSidebarProps> = ({ toggleInProductHelp }) => {
	const {
		isLoggedIn,
		isAdminHubAIEnabled,
		isRovoEnabled: isRovoEnabledSessionData,
	} = useSessionData();
	const isNotesEnabled = useBooleanFeatureFlag('confluence.frontend.notes') && isLoggedIn;

	const isUseRovoEntitlementFFEnabled = useBooleanFeatureFlag(
		`confluence.frontend.use-rovo-entitlement-check`,
	);
	const isAiMateEnabled =
		useBooleanFeatureFlag(`confluence.frontend.ai.mate`) && isLoggedIn && isAdminHubAIEnabled;
	const isRovoEnabled = isUseRovoEntitlementFFEnabled && isRovoEnabledSessionData && isLoggedIn;
	const isConversationAssistantEnabled = isRovoEnabled || isAiMateEnabled;

	const { location, push } = useContext(RoutesContext);

	const [routeData, setRouteData] = useState({
		routeName: '',
		routeGroup: '',
	});
	const inProductHelpContext = useInProductHelpActions();
	const articleId = useInProductHelpArticleId();
	const experienceTracker = useContext(ExperienceTrackerContext);
	const { isPanelOpen } = useQuickstartState();

	useEffect(() => {
		const value = confluenceLocalStorageInstance.getItem(keys.RIGHT_SIDEBAR);
		if (value) {
			try {
				const { panelId, articleId: localStorageArticleId } = JSON.parse(value);
				// Check if there was a specific article previously opened
				if (panelId === HELP_PANEL_ID && localStorageArticleId) {
					inProductHelpContext.setArticleId(localStorageArticleId);
				}
			} catch (e) {
				experienceTracker.stopOnError({
					name: VIEW_IN_PRODUCT_HELP_EXPERIENCE,
					error: e,
				});
			}
		}

		if (location) {
			const { routeName, routeGroup } = getRouteData(location.pathname);
			setRouteData({ routeName, routeGroup });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location, experienceTracker]);

	// Open a panel if one is persisted in local storage
	useEffect(() => {
		const value = confluenceLocalStorageInstance.getItem(keys.RIGHT_SIDEBAR);
		if (value) {
			const { panelId, panelWidth } = JSON.parse(value);

			const isConversationAssistantPanel =
				panelId === CONVERSATION_ASSISTANT_CONTAINER_ID && isConversationAssistantEnabled;
			const isNotesPanel = panelId === NOTES_PANEL_ID && isNotesEnabled;
			const isOtherPanel = ![CONVERSATION_ASSISTANT_CONTAINER_ID, NOTES_PANEL_ID].includes(panelId);

			if (isConversationAssistantPanel || isNotesPanel || isOtherPanel) {
				rightSidebarContext.open(panelId, panelWidth);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const rightSidebarContext = useRightSidebarContext();

	// Remove sidebar item from local storage if no sidebars open in context
	useEffect(() => {
		if (rightSidebarContext.sidebars.length === 0) {
			const value = confluenceLocalStorageInstance.getItem(keys.RIGHT_SIDEBAR);
			if (value) {
				confluenceLocalStorageInstance.removeItem(keys.RIGHT_SIDEBAR);
			}
		}
	}, [rightSidebarContext]);

	const rightSidebarCurrentState = rightSidebarContext.getCurrent();

	const handleOnCloseHelpPanel = () => {
		rightSidebarContext.close(HELP_PANEL_ID);
		// Reset the panel content based on route
		if (location) {
			const { routeName, routeGroup } = getRouteData(location.pathname);
			setRouteData({ routeName, routeGroup });
		}

		confluenceLocalStorageInstance.removeItem(keys.RIGHT_SIDEBAR);

		rightSidebarContext.triggerButtonRef?.current?.focus();
	};

	const handleOnCloseAiMatePanel = () => {
		rightSidebarContext.close(CONVERSATION_ASSISTANT_CONTAINER_ID);
		confluenceLocalStorageInstance.removeItem(keys.RIGHT_SIDEBAR);
	};

	const handleOnCloseIssueCreateSidePanel = () => {
		rightSidebarContext.close(ISSUE_CREATE_SIDE_PANEL_ID);
	};

	const handleOnSetArticleId = (articleId: string) => {
		inProductHelpContext.setArticleId(articleId);
		if (articleId) {
			confluenceLocalStorageInstance.setItem(
				keys.RIGHT_SIDEBAR,
				JSON.stringify({
					panelId: HELP_PANEL_ID,
					panelWidth: HELP_PANEL_WIDTH,
					articleId,
				}),
			);
		}
	};

	const handleOnOpenHelpPanel = useCallback(
		(event: any) => {
			const { detail } = event;
			rightSidebarContext.open(detail.panelId, detail.width, detail.isMinimized, detail.toTop);
			inProductHelpContext.setArticleId(detail.articleId);
		},
		[rightSidebarContext, inProductHelpContext],
	);

	useEffect(() => {
		window.addEventListener('openHelpPanel', handleOnOpenHelpPanel);

		return () => {
			window.removeEventListener('openHelpPanel', handleOnOpenHelpPanel);
		};
	}, [handleOnOpenHelpPanel]);

	/* UseEffect to call toggleInProductHelp based on rightSidebarCurrentState to toggle between
  Quickstart and iph*/
	useEffect(() => {
		const rightSidebarState = rightSidebarContext.getCurrent();
		if (rightSidebarState && !rightSidebarState.isCollapsed && !rightSidebarState.isMinimized) {
			toggleInProductHelp(true);
		} else {
			toggleInProductHelp(false);
		}
	}, [rightSidebarContext, toggleInProductHelp]);

	const shouldDisplaySidebar =
		rightSidebarCurrentState &&
		!rightSidebarCurrentState.isCollapsed &&
		!rightSidebarCurrentState.isMinimized;

	// Open correct sidebar and set localstorage value to persist the panel across refreshes
	const getRightSidePanelElement = () => {
		if (!rightSidebarCurrentState) {
			return;
		}
		const { panelId } = rightSidebarCurrentState;

		if (isNotesEnabled && panelId === NOTES_PANEL_ID) {
			confluenceLocalStorageInstance.setItem(
				keys.RIGHT_SIDEBAR,
				JSON.stringify({
					panelId: NOTES_PANEL_ID,
					panelWidth: NOTES_SIDEBAR_WIDTH,
				}),
			);
			return (
				<ErrorBoundary attribution={Attribution.PAGE_MODES}>
					<NotesSidebar />
				</ErrorBoundary>
			);
		}

		if (isConversationAssistantEnabled && panelId === CONVERSATION_ASSISTANT_CONTAINER_ID) {
			const sidebarStorageItem = JSON.stringify({
				panelId: CONVERSATION_ASSISTANT_CONTAINER_ID,
				panelWidth: CONVERSATION_ASSISTANT_CONTAINER_WIDTH,
			});
			confluenceLocalStorageInstance.setItem(keys.RIGHT_SIDEBAR, sidebarStorageItem);
			return (
				<ErrorBoundary attribution={Attribution.AI_MATE}>
					<ConfluenceConversationAssistant onClosePanel={handleOnCloseAiMatePanel} />
				</ErrorBoundary>
			);
		}

		if (panelId === ISSUE_CREATE_SIDE_PANEL_ID) {
			return (
				<ErrorBoundary attribution={Attribution.BETTER_TOGETHER}>
					<IssueCreateSidePanel onClose={handleOnCloseIssueCreateSidePanel} />
				</ErrorBoundary>
			);
		}

		if (panelId === HELP_PANEL_ID) {
			confluenceLocalStorageInstance.setItem(
				keys.RIGHT_SIDEBAR,
				JSON.stringify({
					panelId: HELP_PANEL_ID,
					panelWidth: HELP_PANEL_WIDTH,
					articleId,
					routeName: routeData.routeName,
					routeGroup: routeData.routeGroup,
				}),
			);
			return (
				<ErrorBoundary attribution={Attribution.COLLABORATION}>
					<HelpPanel
						isOpen
						articleId={articleId}
						push={push}
						routeGroup={routeData.routeGroup}
						routeName={routeData.routeName}
						onCloseHelpPanel={handleOnCloseHelpPanel}
						onSetArticleId={handleOnSetArticleId}
					/>
				</ErrorBoundary>
			);
		}
	};

	if (!shouldDisplaySidebar && isPanelOpen) return null;

	return (
		<RightSidebarPanelWrapper>
			<RightSidebarPanel width={rightSidebarCurrentState?.width || 0} testId="right-sidebar-panel">
				{getRightSidePanelElement()}
			</RightSidebarPanel>
		</RightSidebarPanelWrapper>
	);
};
